import { type SellerUnitAndProject } from "@/types/sup";
import { storage } from "@/lib/storage";
import { createStore } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface BuyerState {
	// Buyer identification
	buyerId: string | null;

	// Favorites management
	favorites: SellerUnitAndProject[];

	// Actions
	setBuyerId: (buyerId: string | null) => void;
	initFavorites: (favorites: SellerUnitAndProject[]) => void;
	setFavorites: (favorites: SellerUnitAndProject[]) => void;
	toggleFavorite: (property: SellerUnitAndProject) => void;
	clearFavorites: () => void;
}

export interface BuyerStoreProps {
	initialBuyerId?: string | null;
	initialFavorites?: SellerUnitAndProject[];
}

export const createBuyerStore = ({ initialBuyerId = null, initialFavorites = [] }: BuyerStoreProps = {}) =>
	createStore<BuyerState>()(
		devtools(
			subscribeWithSelector(
				immer((set, _get) => ({
					buyerId: initialBuyerId,
					favorites: initialFavorites,

					// Buyer ID actions
					setBuyerId: buyerId => {
						try {
							set(state => {
								state.buyerId = buyerId;
							});
						} catch (error) {
							throw error;
						}
					},

					// Favorites actions
					initFavorites: favorites =>
						set(state => {
							if (JSON.stringify(state.favorites) === JSON.stringify(favorites)) return;
							state.favorites = favorites;
						}),

					setFavorites: favorites =>
						set(state => {
							if (JSON.stringify(state.favorites) === JSON.stringify(favorites)) return;
							state.favorites = favorites;
							if (typeof window !== "undefined") {
								storage.setItem("tray_properties", favorites).catch(error => {
									// eslint-disable-next-line no-console
									console.warn("Failed to save favorites to storage:", error);
								});
							}
						}),

					toggleFavorite: property =>
						set(state => {
							const idx = state.favorites.findIndex(fav => fav._id === property._id);
							if (idx !== -1) {
								state.favorites.splice(idx, 1);
							} else {
								state.favorites.push(property);
							}
							// Save to storage
							if (typeof window !== "undefined") {
								storage.setItem("tray_properties", state.favorites).catch(error => {
									// eslint-disable-next-line no-console
									console.warn("Failed to save favorites to storage:", error);
								});
							}
						}),

					clearFavorites: () =>
						set(state => {
							state.favorites = [];
							if (typeof window !== "undefined") {
								storage.removeItem("tray_properties").catch(error => {
									// eslint-disable-next-line no-console
									console.warn("Failed to clear favorites from storage:", error);
								});
							}
						}),
				})),
			),
			{ name: "buyer-store" },
		),
	);
